import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse, IconButton, Stack, Typography } from '@mui/material';
import { Blacklist_New, BlacklistTypes } from 'features/co-pilot/company-blacklists/company-blacklist.types';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import BlacklistItemTable from './blacklist-item-table';

interface ItemProps {
  blacklist: Blacklist_New;
  checked: boolean;
  disabled: boolean;

  handleSelectedBlacklistsChange(): void;
}

const BlacklistItem: React.FC<ItemProps> = ({ blacklist, checked, disabled, handleSelectedBlacklistsChange }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <div className={classes.blacklistItem}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <PaiSwitch checked={checked} disabled={disabled} onClick={handleSelectedBlacklistsChange} />
          <Typography className={classes.regularText}>{_.truncate(blacklist.name || '...', { length: 35 })}</Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={3}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography className={classes.greyText}>|</Typography>
            <Typography className={classes.greyText}>
              {`${blacklist.items?.length ?? 0} ${t(blacklist.blacklistType === BlacklistTypes.COMPANY ? 'companies' : 'leads')} ${t('addedToBlacklist')}`}
            </Typography>
          </Stack>
          <IconButton
            className={cx(classes.expandButton, expanded ? 'expanded' : undefined)}
            size={'small'}
            disabled={_.isEmpty(blacklist.items)}
            onClick={handleToggleExpand}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse className={classes.itemCollapse} in={expanded} timeout={0} unmountOnExit={true}>
        <BlacklistItemTable blacklistType={blacklist.blacklistType} blacklistItems={blacklist.items ?? []} />
      </Collapse>
    </div>
  );
};

interface Props {
  blacklists: Blacklist_New[];
  selectedGlobalBlacklists: string[];
  blacklistSyncLoading: boolean;

  handleSelectedBlacklistsChange(blacklist: Blacklist_New): () => void;
}

const GlobalBlacklistsTable: React.FC<Props> = ({
  blacklists,
  selectedGlobalBlacklists,
  blacklistSyncLoading,
  handleSelectedBlacklistsChange,
}) => {
  const { classes } = useStyles();

  const isChecked = (blacklist: Blacklist_New) => {
    return _.includes(selectedGlobalBlacklists, blacklist._id);
  };

  return (
    <Stack className={classes.blacklistTable} gap={1}>
      {blacklists.map((blacklist) => (
        <BlacklistItem
          key={blacklist._id}
          blacklist={blacklist}
          checked={isChecked(blacklist)}
          disabled={blacklistSyncLoading}
          handleSelectedBlacklistsChange={handleSelectedBlacklistsChange(blacklist)}
        />
      ))}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  blacklistTable: {
    overflow: 'auto',
  },
  blacklistItem: {
    width: '100%',
    padding: '8px 32px',
    background: '#FFF',
    borderRadius: '12px',
  },
  expandButton: {
    padding: '1px',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
      transition: '225ms',
    },
    '&.expanded': {
      '& svg': {
        transform: 'rotate(180deg)',
      },
    },
  },
  itemCollapse: {
    paddingTop: '16px',
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
}));

export default GlobalBlacklistsTable;
